import { useEffect } from 'react'

import { useGrowthBook } from '@growthbook/growthbook-react'

import { getUserId } from 'src/Redux/auth-process/userSlice/selectors'
import {
  useUpdateArtistFeatureFlagsMutation,
  useGetArtistFeatureFlagsQuery,
} from 'src/Redux/A-BFeaturesApi'
import { useAppSelector } from 'src/Hooks/redux'
import {
  usePaidCampaignsCountQuery,
  getHasPaidCampaign,
} from 'src/Redux/metadataApi'
import {
  CampaignPlatformTypeEnum,
  SpotifyCampaignMatchingType,
} from 'src/Types/campaigns'
import { APP } from 'src/Configs/App'

const NEW_ARTIST_MANUAL_MATCHING_FEATURE = 'new_artist_manual_matching'
const EXISTING_ARTIST_MANUAL_MATCHING_FEATURE =
  'existing_artist_manual_matching'

export const useIsManualMatchingEnabled = (): boolean => {
  const { data, isSuccess } = useGetArtistFeatureFlagsQuery(void 0, {
    selectFromResult: (result) => ({
      ...result,
      data: result?.data?.features?.find(
        (item) =>
          item?.name === NEW_ARTIST_MANUAL_MATCHING_FEATURE ||
          item?.name === EXISTING_ARTIST_MANUAL_MATCHING_FEATURE,
      )?.variation,
    }),
  })

  return APP.MANUAL_MATCHING_ENABLED && isSuccess && data === 1
}

export const getScheduleMatchingType = (
  matchingType?: SpotifyCampaignMatchingType | null,
): SpotifyCampaignMatchingType | null => {
  if (
    matchingType === SpotifyCampaignMatchingType.MANUAL &&
    !APP.MANUAL_MATCHING_ENABLED
  ) {
    return SpotifyCampaignMatchingType.FEED
  }

  return matchingType ?? null
}

export const getIsManualMatchingCampaign = (
  matchingType?: SpotifyCampaignMatchingType | null,
): boolean =>
  getScheduleMatchingType(matchingType) === SpotifyCampaignMatchingType.MANUAL

export const useManualMatchingExperiment = (): null => {
  const growthbook = useGrowthBook()
  const isGrowthBookReady = growthbook.ready
  const growthbookAttributes = growthbook.getAttributes()?.id
  const userId = useAppSelector(getUserId)
  const { data: spotifyPaidData, isSuccess: isPaidDataLoaded } =
    usePaidCampaignsCountQuery(CampaignPlatformTypeEnum.SPOTIFY)
  const isNewUser = !getHasPaidCampaign(spotifyPaidData)

  const [addFeatureForUser] = useUpdateArtistFeatureFlagsMutation()

  const { data: hasManualMatchingFeature, isSuccess: featuresIsSucceeded } =
    useGetArtistFeatureFlagsQuery(void 0, {
      selectFromResult: (result) => ({
        ...result,
        data: result?.data?.features?.some(
          (item) =>
            item?.name === NEW_ARTIST_MANUAL_MATCHING_FEATURE ||
            item?.name === EXISTING_ARTIST_MANUAL_MATCHING_FEATURE,
        ),
      }),
    })

  useEffect(() => {
    if (!APP.MANUAL_MATCHING_ENABLED) {
      return
    }

    const isFeatureNeeded =
      featuresIsSucceeded &&
      !hasManualMatchingFeature &&
      isGrowthBookReady &&
      isPaidDataLoaded

    const isAttributesSet = growthbookAttributes === userId

    if (isFeatureNeeded && isAttributesSet) {
      const featureName = isNewUser
        ? NEW_ARTIST_MANUAL_MATCHING_FEATURE
        : EXISTING_ARTIST_MANUAL_MATCHING_FEATURE

      const variant = growthbook.getFeatureValue(featureName, 0)

      addFeatureForUser({
        featureName,
        variation: Number(variant),
      })
    }
  }, [
    addFeatureForUser,
    hasManualMatchingFeature,
    featuresIsSucceeded,
    growthbook,
    isGrowthBookReady,
    isPaidDataLoaded,
    isNewUser,
    growthbookAttributes,
    userId,
  ])

  return null
}
